// The guided tutorial: full-screen and white, fading slowly from screen to screen.
// Welcome → pick software and renderer → choose where to start → steps (text left, picture or video right) → done.
import { esc, html, raw, icon } from '../ui';
import { loadTutorial, mediaKind, chapterFor, type TutorialData, type TutorialChapter, type TutorialMedia, type TutorialStep } from './content';

type Screen =
  | { kind: 'welcome' }
  | { kind: 'software' }
  | { kind: 'chapters' }
  | { kind: 'step'; ci: number; si: number }
  | { kind: 'finish' };

const FADE_OUT = 380;
const store = {
  get: (k: string) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* storage off */ } },
};

/** Short text to HTML: paragraphs, "- " bullets and **bold**. Everything is escaped first. */
export function richText(text: string): string {
  const inline = (s: string) => esc(s).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  return text.trim().split(/\n\s*\n/).map((block) => {
    const lines = block.split('\n').map((l) => l.trim()).filter(Boolean);
    if (lines.length && lines.every((l) => l.startsWith('- '))) return `<ul>${lines.map((l) => `<li>${inline(l.slice(2))}</li>`).join('')}</ul>`;
    return `<p>${lines.map(inline).join('<br>')}</p>`;
  }).join('');
}

export function mediaHtml(m: TutorialMedia | undefined, eager = false): string {
  if (!m?.src) return '<div class="tut-media-empty"></div>';
  const kind = mediaKind(m.src);
  if (kind === 'youtube') {
    const id = m.src.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{11})/)?.[1];
    return id ? `<iframe src="https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1" title="${esc(m.alt ?? 'Video')}" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>` : '';
  }
  if (kind === 'vimeo') {
    const id = m.src.match(/vimeo\.com\/(?:video\/)?(\d+)/)?.[1];
    return id ? `<iframe src="https://player.vimeo.com/video/${id}?dnt=1" title="${esc(m.alt ?? 'Video')}" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>` : '';
  }
  if (kind === 'video') return `<video src="${esc(m.src)}" autoplay muted loop playsinline controls></video>`;
  return `<img src="${esc(m.src)}" alt="${esc(m.alt ?? '')}" ${eager ? '' : 'loading="lazy"'}>`;
}

let open: HTMLElement | null = null;

/** Opens the guide. startAt jumps straight to a chapter (e.g. "upload"); data previews unsaved content. */
export async function openTutorial(opts: { startAt?: string; data?: TutorialData; onDone?: () => void } = {}): Promise<void> {
  if (open) return;
  const data = opts.data ?? (await loadTutorial());
  const root = document.createElement('div');
  root.className = 'tut';
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');
  root.setAttribute('aria-label', 'ArchviewVR guide');
  document.body.append(root);
  document.body.classList.add('tut-open');
  open = root;

  let software = store.get('avr-guide-sw') ?? '';
  let renderer: string | null = store.get('avr-guide-r');
  if (!data.software.some((s) => s.id === software && s.available)) { software = data.software.find((s) => s.available)?.id ?? ''; renderer = null; }

  let chapters: TutorialChapter[] = [];
  const steps = (c: TutorialChapter) => c.steps.filter((s) => !s.hidden);
  const refreshChapters = () => { chapters = data.chapters.filter((c) => chapterFor(c, software, renderer) && steps(c).length); };
  refreshChapters();

  let screen: Screen = { kind: 'welcome' };
  if (opts.startAt) {
    const ci = chapters.findIndex((c) => c.id === opts.startAt);
    if (ci >= 0) screen = { kind: 'step', ci, si: 0 };
  }

  root.innerHTML = html`<div class="tut-top">
      <img class="tut-logo" src="/archviewvr-logo.svg" alt="ArchviewVR">
      <button class="tut-close" id="tut-close">Close guide ${icon('close', 16)}</button>
    </div>
    <div class="tut-stage" id="tut-stage"></div>
    <div class="tut-bottom" id="tut-bottom"></div>`.html;
  const stage = root.querySelector<HTMLElement>('#tut-stage')!;
  const bottom = root.querySelector<HTMLElement>('#tut-bottom')!;

  const close = () => {
    root.classList.remove('on');
    document.removeEventListener('keydown', onKey);
    setTimeout(() => { root.remove(); document.body.classList.remove('tut-open'); open = null; }, 600);
  };

  const next = () => {
    if (screen.kind === 'welcome') go({ kind: 'software' });
    else if (screen.kind === 'software') { if (software) go({ kind: 'chapters' }); }
    else if (screen.kind === 'chapters') go(chapters.length ? { kind: 'step', ci: 0, si: 0 } : { kind: 'finish' });
    else if (screen.kind === 'step') {
      const c = chapters[screen.ci];
      if (screen.si + 1 < steps(c).length) go({ kind: 'step', ci: screen.ci, si: screen.si + 1 });
      else if (screen.ci + 1 < chapters.length) go({ kind: 'step', ci: screen.ci + 1, si: 0 });
      else go({ kind: 'finish' });
    }
  };
  const back = () => {
    if (screen.kind === 'software') go({ kind: 'welcome' });
    else if (screen.kind === 'chapters') go({ kind: 'software' });
    else if (screen.kind === 'step') {
      if (screen.si > 0) go({ kind: 'step', ci: screen.ci, si: screen.si - 1 });
      else if (screen.ci > 0) go({ kind: 'step', ci: screen.ci - 1, si: steps(chapters[screen.ci - 1]).length - 1 });
      else go({ kind: 'chapters' });
    } else if (screen.kind === 'finish') go(chapters.length ? { kind: 'step', ci: chapters.length - 1, si: steps(chapters[chapters.length - 1]).length - 1 } : { kind: 'chapters' });
  };
  const skipPart = () => {
    if (screen.kind !== 'step') return;
    go(screen.ci + 1 < chapters.length ? { kind: 'step', ci: screen.ci + 1, si: 0 } : { kind: 'finish' });
  };

  const onKey = (e: KeyboardEvent) => {
    if ((e.target as HTMLElement).matches?.('input, textarea, select')) return;
    if (e.key === 'Escape') close();
    else if (e.key === 'ArrowRight') next();
    else if (e.key === 'ArrowLeft') back();
  };
  document.addEventListener('keydown', onKey);
  root.querySelector<HTMLButtonElement>('#tut-close')!.onclick = close;

  function drawScreen(): void {
    const s = screen;
    if (s.kind === 'welcome') {
      stage.innerHTML = split(html`<h1 class="tut-hero">${data.welcome.heading}</h1><div class="tut-body">${raw(richText(data.welcome.body))}</div>
        <div class="tut-actions"><button class="btn primary big" data-act="next">Let’s go</button><button class="btn big ghost" data-act="close">Not now</button></div>`.html, data.welcome.media);
    } else if (s.kind === 'software') {
      const sw = data.software.find((x) => x.id === software);
      stage.innerHTML = html`<div class="tut-center">
        <h1>What do you use?</h1>
        <p class="tut-lead">We’ll show the steps for your software.</p>
        <div class="tut-label">3D software</div>
        <div class="tut-choices">${data.software.map((x) => html`<button class="tut-choice" data-sw="${x.id}" aria-pressed="${x.id === software ? 'true' : 'false'}" ${x.available ? '' : raw('disabled')}>${x.label}${x.available ? '' : html`<em>Coming soon</em>`}</button>`)}</div>
        ${sw?.renderers.length ? html`<div class="tut-label">Renderer</div>
          <div class="tut-choices">${sw.renderers.map((r) => html`<button class="tut-choice" data-r="${r.id}" aria-pressed="${r.id === renderer ? 'true' : 'false'}" ${r.available ? '' : raw('disabled')}>${r.label}${r.available ? '' : html`<em>Coming soon</em>`}</button>`)}</div>` : ''}
        <p class="tut-small">Using something else? You can still follow along from <a href="#" data-jump="folder">organising your folder</a>.</p>
      </div>`.html;
      const needsRenderer = !!sw?.renderers.some((r) => r.available);
      stage.querySelectorAll<HTMLButtonElement>('[data-sw]').forEach((b) => (b.onclick = () => {
        software = b.dataset.sw!; renderer = null; store.set('avr-guide-sw', software); refreshChapters(); drawScreen(); drawBottom();
      }));
      stage.querySelectorAll<HTMLButtonElement>('[data-r]').forEach((b) => (b.onclick = () => {
        renderer = b.dataset.r!; store.set('avr-guide-r', renderer); refreshChapters(); drawScreen(); drawBottom();
      }));
      if (needsRenderer && !renderer) {
        const first = sw!.renderers.filter((r) => r.available);
        if (first.length === 1) { renderer = first[0].id; refreshChapters(); stage.querySelector(`[data-r="${renderer}"]`)?.setAttribute('aria-pressed', 'true'); }
      }
    } else if (s.kind === 'chapters') {
      stage.innerHTML = html`<div class="tut-center wide">
        <h1>Where would you like to start?</h1>
        <p class="tut-lead">Already have renders, a points file and a model? Skip ahead to organising your folder or uploading.</p>
        <ol class="tut-chapters">${chapters.map((c, i) => html`<li><button data-ci="${i}"><span class="n">${i + 1}</span><span class="t"><strong>${c.title}</strong><em>${c.summary}</em></span><span class="go">${icon('back', 16)}</span></button></li>`)}</ol>
      </div>`.html;
      stage.querySelectorAll<HTMLButtonElement>('[data-ci]').forEach((b) => (b.onclick = () => go({ kind: 'step', ci: +b.dataset.ci!, si: 0 })));
    } else if (s.kind === 'step') {
      const c = chapters[s.ci]; const list = steps(c); const st: TutorialStep = list[s.si];
      stage.innerHTML = split(html`<div class="tut-eyebrow"><span>${s.ci + 1} · ${c.title}</span>${list.length > 1 ? html`<em>${s.si + 1} of ${list.length}</em>` : ''}</div>
        <h1>${st.heading}</h1><div class="tut-body">${raw(richText(st.body))}</div>
        ${st.download ? html`<a class="btn tut-download" href="${st.download.url}" download>${icon('download', 16)}${st.download.label}</a>` : ''}`.html, st.media);
    } else {
      stage.innerHTML = split(html`<h1 class="tut-hero">${data.finish.heading}</h1><div class="tut-body">${raw(richText(data.finish.body))}</div>
        <div class="tut-actions"><button class="btn primary big" data-act="new">Start a project</button><button class="btn big ghost" data-act="close">Back to projects</button></div>`.html, data.finish.media);
    }
    stage.querySelectorAll<HTMLElement>('[data-act]').forEach((b) => (b.onclick = () => {
      const a = b.dataset.act;
      if (a === 'next') next();
      if (a === 'close') close();
      if (a === 'new') { close(); opts.onDone?.(); location.hash = '#/new'; }
    }));
    stage.querySelectorAll<HTMLElement>('[data-jump]').forEach((a) => (a.onclick = (e) => {
      e.preventDefault();
      const ci = chapters.findIndex((c) => c.id === a.dataset.jump);
      if (ci >= 0) go({ kind: 'step', ci, si: 0 });
    }));
  }

  function drawBottom(): void {
    const s = screen;
    if (s.kind === 'welcome' || s.kind === 'finish') { bottom.innerHTML = ''; return; }
    const progress = s.kind === 'step'
      ? html`<div class="tut-progress">${chapters.map((c, i) => html`<button title="${c.title}" data-ci="${i}" class="${i < s.ci ? 'done' : i === s.ci ? 'on' : ''}"><i style="width:${i < s.ci ? 100 : i === s.ci ? ((s.si + 1) / steps(c).length) * 100 : 0}%"></i></button>`)}</div>`
      : html`<div class="tut-progress"></div>`;
    const canNext = s.kind !== 'software' || !!software && (!data.software.find((x) => x.id === software)?.renderers.some((r) => r.available) || !!renderer);
    bottom.innerHTML = html`${progress}
      <div class="tut-nav">
        <button class="btn ghost" data-nav="back">Back</button>
        ${s.kind === 'step' && s.ci + 1 < chapters.length ? html`<button class="btn ghost" data-nav="skip">Skip this part</button>` : ''}
        ${s.kind === 'chapters' ? '' : html`<button class="btn primary" data-nav="next" ${canNext ? '' : raw('disabled')}>${s.kind === 'step' && s.ci === chapters.length - 1 && s.si === steps(chapters[s.ci]).length - 1 ? 'Finish' : 'Next'}</button>`}
        ${s.kind === 'chapters' ? html`<button class="btn primary" data-nav="next">Start from the beginning</button>` : ''}
      </div>`.html;
    bottom.querySelectorAll<HTMLButtonElement>('[data-nav]').forEach((b) => (b.onclick = () => {
      if (b.dataset.nav === 'back') back();
      if (b.dataset.nav === 'next') next();
      if (b.dataset.nav === 'skip') skipPart();
    }));
    bottom.querySelectorAll<HTMLButtonElement>('.tut-progress [data-ci]').forEach((b) => (b.onclick = () => go({ kind: 'step', ci: +b.dataset.ci!, si: 0 })));
  }

  let busy = false;
  function go(to: Screen): void {
    if (busy) return;
    busy = true;
    stage.classList.add('out');
    setTimeout(() => {
      screen = to;
      drawScreen(); drawBottom();
      stage.scrollTop = 0;
      stage.classList.remove('out');
      busy = false;
      // the next picture loads while this screen is read
      if (to.kind === 'step') {
        const c = chapters[to.ci]; const n = steps(c)[to.si + 1] ?? (chapters[to.ci + 1] ? steps(chapters[to.ci + 1])[0] : null);
        if (n?.media && mediaKind(n.media.src) === 'image') { const im = new Image(); im.src = n.media.src; }
      }
    }, FADE_OUT);
  }

  drawScreen(); drawBottom();
  requestAnimationFrame(() => requestAnimationFrame(() => root.classList.add('on')));
}

function split(text: string, media?: TutorialMedia): string {
  return `<div class="tut-split"><div class="tut-text">${text}</div><div class="tut-media">${mediaHtml(media, true)}</div></div>`;
}
